import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const { login, loginWithGoogle } = useAuth();

  const BLOCKED_STATUSES = ['none', 'viewer', null, undefined];

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);

    const result = await loginWithGoogle();

    if (!result.success) {
      toast.error('Erro ao fazer login com Google. Verifique se seu usuário está cadastrado no sistema.');
      setGoogleLoading(false);
      return;
    }

    if (BLOCKED_STATUSES.includes(result.organizerStatus)) {
      toast.error('Usuário sem acesso ao painel de organizadores');
      setGoogleLoading(false);
      return;
    }

    toast.success('Login realizado com sucesso!');
    setGoogleLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (!result.success) {
      toast.error('Erro ao fazer login. Verifique suas credenciais.');
      setLoading(false);
      return;
    }

    if (BLOCKED_STATUSES.includes(result.organizerStatus)) {
      toast.error('Usuário sem acesso ao painel de organizadores');
      setLoading(false);
      return;
    }

    toast.success('Login realizado com sucesso!');
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-xl px-8 pt-5 pb-8 shadow-sm">
          <div className="flex flex-col items-center mb-3 text-center">
            <img
              src="/logo_eventfy.png"
              alt="Joyn"
              className="w-72 h-auto object-contain"
            />
          </div>

          <div className="space-y-6">
            <Button
              type="button"
              variant="outline"
              className="w-full gap-3 h-12"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {googleLoading ? 'Entrando...' : 'Entrar com Google'}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Ou continue com email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full gap-2"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                <LogIn className="w-4 h-4" />
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>

              <div className="flex justify-center items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setPrivacyOpen(true)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Privacidade
                </button>
                <span className="text-xs text-muted-foreground">|</span>
                <button
                  type="button"
                  onClick={() => setTermsOpen(true)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Termos
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modal Privacidade */}
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Política de Privacidade – Joyn</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-1 text-sm text-muted-foreground leading-relaxed space-y-4">
            <p className="text-xs text-muted-foreground">Última atualização: Junho de 2026</p>
            <p>O Joyn respeita sua privacidade e está comprometido em proteger seus dados pessoais. Esta Política de Privacidade explica quais informações coletamos, como elas são utilizadas e quais são seus direitos ao utilizar o aplicativo.</p>

            <div>
              <p className="font-semibold text-foreground mb-1">1. Dados coletados</p>
              <p>Ao utilizar o Joyn, podemos coletar as seguintes informações:</p>
              <ul className="mt-1 space-y-1 list-none">
                <li>• Nome e endereço de e-mail fornecidos durante o cadastro.</li>
                <li>• Número de telefone, quando informado pelo usuário.</li>
                <li>• Foto de perfil enviada pelo usuário ou obtida através do login Google.</li>
                <li>• Localização aproximada ou precisa, mediante autorização do usuário, para exibir eventos próximos.</li>
                <li>• Informações de uso do aplicativo, como telas acessadas, interações e preferências.</li>
                <li>• Identificadores técnicos do dispositivo necessários para funcionamento, segurança e análise do aplicativo.</li>
              </ul>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">2. Como utilizamos seus dados</p>
              <p>Os dados coletados podem ser utilizados para:</p>
              <ul className="mt-1 space-y-1 list-none">
                <li>• Criar e gerenciar sua conta.</li>
                <li>• Exibir eventos relevantes de acordo com sua localização.</li>
                <li>• Personalizar sua experiência dentro do aplicativo.</li>
                <li>• Permitir funcionalidades como favoritos, agenda e inscrições em eventos.</li>
                <li>• Melhorar desempenho, estabilidade e segurança do aplicativo.</li>
                <li>• Realizar análises estatísticas sobre o uso da plataforma.</li>
              </ul>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">3. Compartilhamento de dados</p>
              <p>O Joyn não vende informações pessoais dos usuários.</p>
              <p className="mt-1">Os dados podem ser processados por provedores de serviços essenciais para o funcionamento da plataforma, incluindo:</p>
              <ul className="mt-1 space-y-1 list-none">
                <li>• Google Firebase (autenticação, banco de dados, armazenamento, analytics e notificações).</li>
                <li>• Google Maps Platform (serviços de localização e mapas).</li>
              </ul>
              <p className="mt-1">Esses serviços possuem suas próprias políticas de privacidade.</p>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">4. Notificações</p>
              <p>O Joyn poderá enviar notificações relacionadas a eventos, atualizações da plataforma e outras informações relevantes. O usuário pode desativar as notificações nas configurações do dispositivo.</p>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">5. Segurança</p>
              <p>Adotamos medidas técnicas e organizacionais para proteger os dados dos usuários contra acesso não autorizado, alteração, divulgação ou destruição.</p>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">6. Retenção de dados</p>
              <p>Os dados serão mantidos enquanto forem necessários para a prestação dos serviços ou para cumprimento de obrigações legais.</p>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">7. Exclusão da conta e dos dados</p>
              <p>O usuário pode solicitar a exclusão de sua conta e dos dados associados a qualquer momento através dos canais de contato informados abaixo.</p>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">8. Direitos do usuário</p>
              <p>O usuário pode solicitar acesso, correção ou exclusão de seus dados pessoais, conforme aplicável pela legislação vigente.</p>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">9. Alterações nesta política</p>
              <p>Esta Política de Privacidade poderá ser atualizada periodicamente. Alterações relevantes serão comunicadas através do aplicativo ou de outros meios apropriados.</p>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">10. Contato</p>
              <p>Em caso de dúvidas, solicitações relacionadas à privacidade ou pedidos de exclusão de dados, entre em contato:</p>
              <a href="mailto:oficialjoyn@gmail.com" className="text-primary hover:underline">oficialjoyn@gmail.com</a>
            </div>

            <p className="pt-1">Ao utilizar o Joyn, você concorda com os termos desta Política de Privacidade.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Termos */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Termos de Uso – Joyn</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Os Termos de Uso do Joyn estarão disponíveis em breve.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};
